import axios, { AxiosInstance, AxiosResponse } from "axios";
import qs from "qs";
import { AxiosWrapper } from "services/AxiosWrapper";
import { ParameterService } from "services/ParametersProviderApi/ParametersService";
import { Conversation, GetCategoriesReturnType, GetMessagesReturnType, GetPromptByCategoryReturnType, ListChatsReturnType, SendMessageReturnType, SendNewMessageReturnType } from "./types";
import { injectJwtTokenIntoHeaders } from "services/tokenLocalStorage";

export const BackendEndpoints = {
  sendNewMessage: () => 'conversations/new',
  sendMessage: (id: string) => `conversations/${id}/messages`,
  listChats: () => 'conversations/list',
  searchMessages: () => 'messages/search',
  getMessages: (id: string) => `conversations/${id}/messages`,
  pinUnpinChat: (id: string) => `conversations/${id}/pin`,
  renameChat: (id: string) => `conversations/${id}/rename`,
  deleteChat: (id: string) => `conversations/${id}`,
  downloadChat: (id: string) => `conversations/${id}/download`,
  rateMessage: (id: string) => `messages/${id}/rate`,
  getCategories: () => 'categories',
  getPromptsByCategory: (categoryId: string) => `categories/${categoryId}/prompts`,
};

export interface IChatApiService<T extends AxiosWrapper<AxiosInstance>> {
  client: T;
  sendNewMessage(message: string): Promise<AxiosResponse<SendNewMessageReturnType>>;

  sendMessage(conversationId: string, message: string): Promise<AxiosResponse<SendMessageReturnType>>;

  listChats(): Promise<AxiosResponse<ListChatsReturnType>>;

  getMessages(
    conversationId: string,
    page: number,
    limit: number
  ): Promise<AxiosResponse<GetMessagesReturnType>>;

  renameChat(conversationId: string, newName: string): Promise<AxiosResponse<void>>;

  deleteChat(conversationId: string): Promise<AxiosResponse<void>>;

  pinUnpinChat(pin: boolean, conversationId: string): Promise<AxiosResponse<void>>;

  rateMessage(rating: boolean, messageId: string): Promise<AxiosResponse<void>>;

  searchMessages(keywords: string): Promise<AxiosResponse<Conversation[]>>;

  getCategories(): Promise<AxiosResponse<GetCategoriesReturnType>>;

  getPromptsByCategory(categoryId: string): Promise<AxiosResponse<GetPromptByCategoryReturnType>>;

  downloadChat(conversationId: string): Promise<AxiosResponse<Blob>>;
}



export class ChatApiService<T extends AxiosWrapper<AxiosInstance>> implements IChatApiService<T> {
  client: T;

  constructor(client: T) {
    this.client = client;
  }

  async sendNewMessage(message: string) {
    return await this.client.post<SendNewMessageReturnType>(BackendEndpoints.sendNewMessage(), { message });
  }

  async sendMessage(conversationId: string, message: string) {
    return await this.client.post<SendMessageReturnType>(BackendEndpoints.sendMessage(conversationId), { message });
  }

  async listChats() {
    return await this.client.get<ListChatsReturnType>(BackendEndpoints.listChats());
  }

  async getMessages(conversationId: string, page: number, limit: number) {
    return await this.client.get<GetMessagesReturnType>(
      BackendEndpoints.getMessages(conversationId),
      { params: { page, limit } }
    );
  }

  async renameChat(conversationId: string, newName: string) {
    return await this.client.patch<void>(BackendEndpoints.renameChat(conversationId), { newName });
  }

  async deleteChat(conversationId: string) {
    return await this.client.delete<void>(BackendEndpoints.deleteChat(conversationId));
  }

  async searchMessages(keywords: string) {
    return await this.client.post<Conversation[]>(BackendEndpoints.searchMessages(), {
      keywords 
    });
  }

  async pinUnpinChat(pin: boolean, conversationId: string) {
    return await this.client.post<void>(BackendEndpoints.pinUnpinChat(conversationId), {
      pin
    });
  }

  async rateMessage(rating: boolean, messageId: string) {
    return await this.client.post<void>(BackendEndpoints.rateMessage(messageId), {
      rating 
    });
  }

  async getCategories() {
    return await this.client.get<GetCategoriesReturnType>(BackendEndpoints.getCategories());
  }

  async getPromptsByCategory(categoryId: string) {
    return await this.client.get<GetPromptByCategoryReturnType>(BackendEndpoints.getPromptsByCategory(categoryId));
  }

  async downloadChat(conversationId: string) {
    return await this.client.get<Blob>(BackendEndpoints.downloadChat(conversationId), {
      responseType: "blob",
    });
  }
}

export const makeChatApi = (): ChatApiService<AxiosWrapper<AxiosInstance>> => {
  const dynamicParameters = ParameterService.getParameters();
  const headers: any = {};

  const client = axios.create({
    baseURL: dynamicParameters.CHATBOT_BASE_URL,
    headers: injectJwtTokenIntoHeaders(headers),
    withCredentials: true,
    paramsSerializer: params => {
      return qs.stringify(params);
    },
  });

  const clientWrapper = new AxiosWrapper(client);

  return new ChatApiService<AxiosWrapper<AxiosInstance>>(clientWrapper);
};
